// client/src/pages/EditTask.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_API_URL}/api`;

const CATEGORY_OPTIONS = [
  "Assignment",
  "PPT",
  "Drawing",
  "Technical Help",
  "Event Help",
  "Design",
  "Other",
];

export default function EditTask() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [customCategory, setCustomCategory] = useState(""); // FIXED LOGIC
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  useEffect(() => {
    if (!id) return navigate("/profile");

    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/tasks/${id}`);
        const d = await res.json();
        if (!res.ok) throw new Error(d.error || "Failed to load");

        const t = d.task || d;
        if (!t) throw new Error("Task not found");

        if (String(t.postedBy) !== String(user?.id)) {
          alert("Not allowed");
          return navigate("/profile");
        }

        setTask(t);
        setTitle(t.title || "");
        setDescription(t.description || "");
        setCategory(t.category || "");
        setCustomCategory(t.category === "Other" ? "" : ""); // safe fallback
        setBudgetMin(t.budgetMin ?? "");
        setBudgetMax(t.budgetMax ?? "");
        setDueDate(
          t.dueDate ? new Date(t.dueDate).toISOString().slice(0, 16) : ""
        );
        setImagePreview(t.imageUrl || "");
      } catch (err) {
        console.error(err);
        alert(err.message || "Failed to load task");
        navigate("/profile");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate, user]);

  // UPLOAD
  const uploadImage = async () => {
    if (!imageFile) return imagePreview || null;

    const fd = new FormData();
    fd.append("image", imageFile);

    const token = localStorage.getItem("cp_token");
    const res = await fetch(`${API}/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });

    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.error || "Upload failed");
    }

    const data = await res.json();
    return data.url || data.imageUrl || data.path || null;
  };

  const handleSave = async () => {
    if (!user) return navigate("/login");
    if (!task) return;

    setSaving(true);
    try {
      const token = localStorage.getItem("cp_token");

      if (dueDate && new Date(dueDate) <= new Date()) {
        alert("Due date must be in the future.");
        setSaving(false);
        return;
      }

      let imageUrl = imagePreview;

      if (imageFile) {
        const up = await uploadImage();
        if (up) imageUrl = up;
      }

      const finalCategory =
        category === "Other" && customCategory.trim() !== ""
          ? customCategory.trim()
          : category;

      const payload = {
        title,
        description,
        category: finalCategory,
        budgetMin: Number(budgetMin) || 0,
        budgetMax: Number(budgetMax) || 0,
        dueDate: dueDate ? new Date(dueDate).toISOString() : null,
        imageUrl,
      };

      const res = await fetch(`${API}/tasks/${id}/edit`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Save failed");

      setImageFile(null); // small fix
      alert("Task updated");

      navigate(`/task/${id}`); // FIX: correct detail route
    } catch (err) {
      console.error(err);
      alert(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6">Loading…</div>;
  if (!task) return <div className="p-6">Task not found</div>;

  return (
    <div className="min-h-screen p-6 bg-slate-50 dark:bg-slate-900">
      <div className="max-w-3xl mx-auto bg-white dark:bg-slate-800 p-6 rounded-xl shadow">
        <h2 className="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
          Edit Task
        </h2>

        <div className="space-y-4">
          {/* TITLE */}
          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-300">
              Title
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full mt-1 p-2 border rounded dark:bg-slate-700 dark:text-white"
            />
          </div>

          {/* DESCRIPTION */}
          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-300">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full mt-1 p-2 border rounded dark:bg-slate-700 dark:text-white"
              rows={5}
            />
          </div>

          {/* CATEGORY */}
          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-300">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full mt-1 p-2 border rounded dark:bg-slate-700 dark:text-white"
            >
              <option value="">-- Select a Category --</option>
              {CATEGORY_OPTIONS.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            {/* FIXED CUSTOM CATEGORY FIELD */}
            {category === "Other" && (
              <input
                placeholder="Enter custom category"
                className="w-full mt-2 p-2 border rounded dark:bg-slate-700 dark:text-white"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
              />
            )}
          </div>

          {/* BUDGET */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-sm text-slate-600 dark:text-slate-300">
                Budget Min
              </label>
              <input
                type="number"
                value={budgetMin}
                onChange={(e) => setBudgetMin(e.target.value)}
                className="w-full mt-1 p-2 border rounded dark:bg-slate-700 dark:text-white"
              />
            </div>

            <div className="flex-1">
              <label className="block text-sm text-slate-600 dark:text-slate-300">
                Budget Max
              </label>
              <input
                type="number"
                value={budgetMax}
                onChange={(e) => setBudgetMax(e.target.value)}
                className="w-full mt-1 p-2 border rounded dark:bg-slate-700 dark:text-white"
              />
            </div>
          </div>

          {/* DEADLINE */}
          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-300">
              Due Date
            </label>
            <input
              type="datetime-local"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full mt-1 p-2 border rounded dark:bg-slate-700 dark:text-white"
            />
          </div>

          {/* IMAGE */}
          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-300">
              Image
            </label>
            <div className="flex items-center gap-4 mt-2">
              <div className="w-28 h-20 bg-slate-100 rounded overflow-hidden">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400">
                    No
                  </div>
                )}
              </div>

              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setImageFile(f);
                    setImagePreview(URL.createObjectURL(f));
                  }
                }}
              />
            </div>
          </div>

          {/* BUTTONS */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 text-white rounded"
            >
              {saving ? "Saving…" : "Save"}
            </button>

            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 border rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
